/**
 * Which use cases open which surface. One definition, because more than one name can open the same page.
 *
 * `hbs-chat` and `healthcare-chat-v2` both navigate to /ask-natural-organic — see
 * `LandingScreen.handleUseCaseClick`, where the hbs tile opens the same path in a new tab. A route gated on one
 * of them refuses a client who legitimately holds the other; that happened to hbs and was measured as a 200
 * becoming a 403. Mirrors `CHAT_SURFACE_USE_CASES` on the backend.
 */
export const FOCUS_GROUP_CHAT_USE_CASES = ['healthcare-chat-v2', 'hbs-chat'] as const;

export const BLUEPILL_CHAT_USE_CASES = ['bluepill-chat'] as const;

/** The backend's "no restriction" sentinel for a use-case list. */
export const ALL_USE_CASES = 'all';

/**
 * Does this `allowedUseCases` value restrict anything?
 *
 * FOUR values mean "unrestricted", and every one of them has bitten someone:
 *   `undefined` / `null` — a backend deployed behind this build reports no use cases. Reading that as "none"
 *                          empties the product for every client at once.
 *   `'all'`             — the sentinel, which every plan but `summit` resolves to.
 *   `[]`                — a plan nobody has configured. `LandingScreen` has always normalised this to
 *                          unrestricted, and `LandingUseCaseAllowList.test.tsx` locks it in: "a plan whose list
 *                          has not been configured must not lock its users out of the product."
 *
 * Shared rather than duplicated because the tile filter and the route guard MUST agree. They did not: the guard
 * special-cased the first three and let `[]` fall through to "deny", so a plan with an empty list showed the
 * Focus Group tile and then bounced the click straight back to the dashboard — a dead-end tile, which is the exact
 * bug class the route guard exists to remove, inverted. Found in review of #734.
 *
 * Mirrors `is_unrestricted` on the backend, which reads `not capabilities` the same way.
 */
export const isUnrestrictedUseCases = (
  allowedUseCases: string[] | string | null | undefined,
): boolean =>
  allowedUseCases === undefined ||
  allowedUseCases === null ||
  allowedUseCases === ALL_USE_CASES ||
  (Array.isArray(allowedUseCases) && allowedUseCases.length === 0);
